import { t } from '@lingui/core/macro';
import type { BookingGarageRecordCreate, LicensePlate, TireSeason } from '@navio/nsdk';
import { PaymentOption } from '@navio/nsdk';
import type { BookingUserNotificationDto } from 'noddi-types';
import { getReservationRemainingMs } from 'noddi-util';
import type { StationarySlotHold } from 'noddi-web';

/** Whether the confirm CTA should open pay-before (Dintero) after create. */
export function shouldPayBeforeOnConfirm(paymentOption: PaymentOption, totalAmount: number): boolean {
  return paymentOption === PaymentOption.PAY_BEFORE && totalAmount > 0;
}

/** First blocking message for a confirm click, or null when the click may proceed. */
export function getStationaryConfirmClickBlocker(args: {
  isLoggedIn: boolean;
  isReservationExpired: boolean;
  hasValidPhone: boolean;
  hasOrganizationNumber: boolean;
}): string | null {
  const { isLoggedIn, isReservationExpired, hasValidPhone, hasOrganizationNumber } = args;

  if (!isLoggedIn) {
    return null;
  }
  if (isReservationExpired) {
    return t`Your reserved time slot expired. Please select a time again.`;
  }
  if (!hasValidPhone) {
    return t`Please update your phone number on your account before confirming`;
  }
  if (!hasOrganizationNumber) {
    return t`Please add an organization number before confirming`;
  }
  return null;
}

/** Primary confirm CTA copy from the real payment option (not a collapsed boolean). */
export function getConfirmCtaText(paymentOption: PaymentOption, shouldPayBefore: boolean): string {
  if (shouldPayBefore) {
    return t`Confirm and pay`;
  }
  if (paymentOption === PaymentOption.INVOICE) {
    return t`Confirm booking with invoice payment`;
  }
  return t`Confirm booking`;
}

/** Invoice reference the customer typed, and whether the payer requires one. */
export type InvoiceReferenceInput = { value: string; required: boolean };

/** Message blocking confirm while a required invoice reference is empty, or null. */
export function getInvoiceReferenceBlocker(invoiceReference: InvoiceReferenceInput | undefined): string | null {
  if (invoiceReference?.required && !invoiceReference.value.trim()) {
    return t`Enter an invoice reference.`;
  }
  return null;
}

export type StationaryCreateBodyResult =
  | { ok: true; body: Omit<BookingGarageRecordCreate, 'payment'> }
  | { ok: false; message: string };

type BuildStationaryCreateBodyInput = {
  licensePlate?: LicensePlate | null;
  hold: StationarySlotHold | null;
  salesItemIds: number[];
  userId?: number;
  userGroupId?: number | null;
  brandDomain: string;
  bookingProposalId?: number;
  /** Set only when the customer chose invoice and the recipient is not themselves. */
  userGroupPayerId?: number | null;
  /** Per-booking SMS recipients; omitted means the backend default (the car's managers). */
  notificationsToOverride?: BookingUserNotificationDto[];
  /** The wheel set that goes into storage, when the booking signs the car up for the tire hotel. */
  incomingTireSeason?: TireSeason | null;
  /** Override default message when the slot hold is missing/expired. */
  missingHoldMessage?: string;
  /** Override default incomplete-fields message (car, service, user, org). */
  incompleteMessage?: string;
};

/** Pure create-at-station payload (payment applied by the confirm CTA). */
export function buildStationaryCreateBody({
  licensePlate,
  hold,
  salesItemIds,
  userId,
  userGroupId,
  brandDomain,
  bookingProposalId,
  userGroupPayerId,
  notificationsToOverride,
  incomingTireSeason,
  missingHoldMessage,
  incompleteMessage
}: BuildStationaryCreateBodyInput): StationaryCreateBodyResult {
  const primarySalesItemId = salesItemIds[0];

  // Missing hold is usually an expired reservation cleared from the store — not a
  // half-filled form. Surface a time-slot message so users re-pick instead of hunting fields.
  if (!hold) {
    return {
      ok: false,
      message: missingHoldMessage ?? t`Your reserved time slot expired. Please select a time again.`
    };
  }

  // Billing collection can outlast the hold after the initial confirm click.
  if ((getReservationRemainingMs(hold.reservedUntil) ?? 0) <= 0) {
    return {
      ok: false,
      message: t`Your reserved time slot expired. Please select a time again.`
    };
  }

  if (!licensePlate || primarySalesItemId == null || !userGroupId || userId == null) {
    return {
      ok: false,
      message: incompleteMessage ?? t`Please fill in all required fields to confirm the booking`
    };
  }

  return {
    ok: true,
    body: {
      cars: [
        {
          licensePlate,
          selectedSalesItemIds: salesItemIds,
          incomingTireSeason
        }
      ],
      startsAt: hold.startsAt,
      endsAt: hold.endsAt,
      workstationId: hold.workstationId,
      userId,
      userGroupId,
      reservationTicket: hold.ticket ?? undefined,
      reservationSecret: hold.secret ?? undefined,
      brandDomain,
      ...(bookingProposalId != null ? { bookingProposalId } : {}),
      ...(userGroupPayerId != null ? { userGroupPayerId } : {}),
      ...(notificationsToOverride?.length ? { notificationsToOverride } : {})
    }
  };
}
